import React from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

type ServingSizeLike = {
  servingId?: string;
  label?: string | null;
  grams?: number | null;
  quantity?: number | null;
  isDefault?: boolean;
};

type RecipeServingInfoPopoverProps = {
  defaultServingId?: string | null;
  defaultServingGrams?: number | null;
  selectedServingId?: string | null;
  selectedServingGrams?: number | null;
  servingSizes?: ServingSizeLike[] | null;
  className?: string;
  iconClassName?: string;
};

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const normalizeServingId = (value?: string | null): string => String(value ?? '').trim();

const formatServingUnitName = (value: string, quantity: number): string => {
  const normalized = formatUnderscoreDisplayLabel(value).toLowerCase();
  if (!normalized) return '';
  if (quantity === 1 || normalized.endsWith('s')) return normalized;
  return `${normalized}s`;
};

const formatPieceQuantity = (quantity: number): string => {
  const pieceLabel = quantity === 1 ? 'piece' : 'pieces';
  return `${formatNumber(quantity)} ${pieceLabel}`;
};

const formatServingWithQuantity = (
  label: string,
  grams?: number | null,
  quantity?: number | null,
  servingId?: string,
): string => {
  const numericGrams = Number(grams);
  const numericQuantity = Number(quantity);
  const hasPieceQuantity = Number.isFinite(numericQuantity) && numericQuantity > 0 && Math.abs(numericQuantity - 1) >= 0.01;
  const hasGrams = Number.isFinite(numericGrams) && numericGrams > 0;
  const servingName = formatServingUnitName(servingId && servingId !== label ? servingId : label, 1) || label.toLowerCase();
  const parts = [
    hasPieceQuantity ? formatPieceQuantity(numericQuantity) : null,
    hasGrams ? `${formatNumber(numericGrams)}g` : null,
  ].filter((part): part is string => Boolean(part));

  if (parts.length > 0) {
    return `1 ${servingName} (${parts.join(', ')})`;
  }

  return `1 ${servingName}`;
};

export const getRecipeServingDisplayValue = ({
  defaultServingId,
  defaultServingGrams,
  selectedServingId,
  selectedServingGrams,
}: Omit<RecipeServingInfoPopoverProps, 'servingSizes' | 'className' | 'iconClassName'>): string => {
  const effectiveServingId = normalizeServingId(selectedServingId) || normalizeServingId(defaultServingId);
  const effectiveServingLabel = formatServingUnitName(effectiveServingId, 1);
  const effectiveGrams = Number(selectedServingGrams ?? defaultServingGrams);

  if (effectiveServingLabel && Number.isFinite(effectiveGrams) && effectiveGrams > 0) {
    return `1 ${effectiveServingLabel} = ${formatNumber(effectiveGrams)}g`;
  }
  if (effectiveServingLabel) {
    return `1 ${effectiveServingLabel}`;
  }
  if (Number.isFinite(effectiveGrams) && effectiveGrams > 0) {
    return `${formatNumber(effectiveGrams)}g`;
  }
  return '1 serving';
};

const RecipeServingInfoPopover: React.FC<RecipeServingInfoPopoverProps> = ({
  defaultServingId,
  defaultServingGrams,
  selectedServingId,
  selectedServingGrams,
  servingSizes = [],
  className,
  iconClassName,
}) => {
  const selectedId = normalizeServingId(selectedServingId);
  const defaultId = normalizeServingId(defaultServingId);
  const selectedGrams = Number(selectedServingGrams);
  const defaultGrams = Number(defaultServingGrams);
  const selectedDisplay = getRecipeServingDisplayValue({
    defaultServingId,
    defaultServingGrams,
    selectedServingId,
    selectedServingGrams,
  });
  const baseServingLabel = formatServingUnitName(defaultId || selectedId || 'serving', 1) || 'serving';
  const baseServingText = Number.isFinite(defaultGrams) && defaultGrams > 0
    ? `${formatNumber(defaultGrams)}g ${baseServingLabel}`
    : `1 ${baseServingLabel}`;
  const defaultLabel = formatServingUnitName(defaultId, 1);
  const selectedLabel = formatServingUnitName(selectedId, 1);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn('inline-flex items-center', className)}
          aria-label="Serving size info"
        >
          <Info className={cn('h-4 w-4', iconClassName)} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 text-xs leading-relaxed">
        <p className="text-slate-700">
          These ingredients are defined and this recipe is calculated for a {baseServingText}.
        </p>
        <p className="mt-2 text-slate-600">
          If you change the serving size, we&apos;ll recalculate the ingredients and nutrition for your selected portion.
        </p>
        <p className="mt-2 text-slate-600">Current selection: {selectedDisplay}</p>
        {defaultId ? (
          <p className="mt-1 text-slate-600">
            Default serving: 1 {defaultLabel}
            {Number.isFinite(defaultGrams) && defaultGrams > 0 ? ` (${formatNumber(defaultGrams)}g)` : ''}
          </p>
        ) : null}
        {selectedId && selectedId !== defaultId ? (
          <p className="mt-1 text-slate-600">
            Selected serving: 1 {selectedLabel}
            {Number.isFinite(selectedGrams) && selectedGrams > 0 ? ` (${formatNumber(selectedGrams)}g)` : ''}
          </p>
        ) : null}
        {Array.isArray(servingSizes) && servingSizes.length > 0 ? (
          <div className="mt-2 border-t border-slate-100 pt-2 text-slate-600">
            {servingSizes.slice(0, 4).map((size, index) => {
              const servingId = normalizeServingId(size.servingId) || `serving_${index + 1}`;
              const grams = Number(size.grams);
              const label = formatUnderscoreDisplayLabel(size.label || servingId);
              return (
                <p key={`${servingId}-${index}`}>
                  {formatServingWithQuantity(label, grams, size.quantity, servingId)}
                </p>
              );
            })}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

export default RecipeServingInfoPopover;
